import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import QRCode from 'qrcode'
import numberToVietnameseWords from '../../../../helpers/numberToVietnameseWords'
import {
    COMPANY_NAME, DEFAULT_REASON, DEFAULT_UNIT, DEPARTMENT_NAME, EMPTY_VALUE, WAREHOUSE_NAME,
    formatPrintDateTime, formatSlipDate, getSlipTotals,
} from '../../helpers/print'

const WarehouseSlip = ({ handover, printInfo }) => {
    const {
        code, partner_snapshot: partner, handed_at: handedAt, handed_by: handedBy, lines,
        print_count: printCount, last_printed_at: lastPrintedAt, last_printed_by: lastPrintedBy,
    } = handover
    const [qrUrl, setQrUrl] = useState('')

    useEffect(() => {
        let cancelled = false
        setQrUrl('')
        if (!code) return undefined

        QRCode.toDataURL(code, { width: 160, margin: 1 })
            .then(url => { if (!cancelled) setQrUrl(url) })
            .catch(() => { if (!cancelled) setQrUrl('') })

        return () => { cancelled = true }
    }, [code])

    const slipLines = lines || []
    const { totalBatches, totalQuantity } = getSlipTotals(slipLines)
    const quantityInWords = numberToVietnameseWords(totalQuantity)
    const reason = `${printInfo.reason || ''}`.trim() || DEFAULT_REASON
    const handedByName = (handedBy && handedBy.username) || ''
    const printedByName = (lastPrintedBy && lastPrintedBy.username) || ''
    // Thiếu thông tin người nhận thì để dòng chấm cho ghi tay.
    const _orBlank = value => `${value || ''}`.trim() || EMPTY_VALUE

    const signatures = [
        { title: 'Người lập phiếu', name: printedByName },
        { title: 'Người giao hàng', name: handedByName },
        { title: 'Người nhận hàng', name: `${printInfo.receiver_name || ''}`.trim() },
        { title: 'Thủ kho / Quản lý', name: '' },
    ]

    return (
        <div className='WarehouseSlip'>
            {printCount >= 2 && (
                <div className='WarehouseSlip__reprint'>BẢN IN LẠI · lần {printCount}</div>
            )}

            <div className='WarehouseSlip__header'>
                <div className='WarehouseSlip__company'>
                    <b>{COMPANY_NAME}</b>
                    Kho xuất: {WAREHOUSE_NAME}<br />
                    Bộ phận lập: {DEPARTMENT_NAME}
                </div>
                <div className='WarehouseSlip__qr'>
                    {qrUrl && <img src={qrUrl} alt={`QR ${code}`} />}
                    {code}
                </div>
            </div>

            <h1 className='WarehouseSlip__title'>PHIẾU XUẤT KHO</h1>
            <div className='WarehouseSlip__subtitle'>{formatSlipDate(handedAt)} · Số: {code}</div>

            <div className='WarehouseSlip__fields'>
                <p>Đối tác may: <b>{(partner && partner.name) || ''}</b></p>
                <p>Thời gian giao: <b>{formatPrintDateTime(handedAt)}</b></p>
                <p>Họ tên người nhận hàng: <b>{_orBlank(printInfo.receiver_name)}</b></p>
                <p>Số điện thoại: <b>{_orBlank(printInfo.receiver_phone)}</b></p>
                <p className='WarehouseSlip__fieldFull'>Địa chỉ: <span>{_orBlank(printInfo.receiver_address)}</span></p>
                <p>Lý do xuất kho: <span>{reason}</span></p>
            </div>

            <table className='WarehouseSlip__lines'>
                <thead>
                    <tr>
                        <th className='WarehouseSlip__colIndex'>STT</th>
                        <th>Tên sản phẩm (mã SKU)</th>
                        <th className='WarehouseSlip__colBatch'>Tên lô</th>
                        <th className='WarehouseSlip__colUnit'>ĐVT</th>
                        <th className='WarehouseSlip__colQuantity'>Số lượng</th>
                    </tr>
                </thead>
                <tbody>
                    {slipLines.map((line, index) => (
                        <tr key={`${line.product_name}-${line.batch_name}-${index}`}>
                            <td className='WarehouseSlip__colIndex'>{index + 1}</td>
                            <td>{line.product_name}</td>
                            <td className='WarehouseSlip__colBatch'>{line.batch_name}</td>
                            <td className='WarehouseSlip__colUnit'>{line.unit || DEFAULT_UNIT}</td>
                            <td className='WarehouseSlip__colQuantity'>{line.quantity}</td>
                        </tr>
                    ))}
                    <tr className='WarehouseSlip__total'>
                        <td />
                        <td colSpan={3}>TỔNG CỘNG ({totalBatches} lô)</td>
                        <td className='WarehouseSlip__colQuantity'>{totalQuantity}</td>
                    </tr>
                </tbody>
            </table>

            <p className='WarehouseSlip__words'>
                Tổng số lượng (viết bằng chữ): <i>{quantityInWords && `${quantityInWords} ${DEFAULT_UNIT.toLowerCase()}`}</i>
            </p>
            <p className='WarehouseSlip__note'>
                Ghi chú: ............................................................................................................................
            </p>

            <div className='WarehouseSlip__signatures'>
                {signatures.map(({ title, name }) => (
                    <div key={title}>
                        <b>{title}</b>
                        <i>(Ký, họ tên)</i>
                        <div className='WarehouseSlip__signSpace' />
                        {name}
                    </div>
                ))}
            </div>

            {printCount > 0 && (
                <div className='WarehouseSlip__footer'>
                    In lúc {formatPrintDateTime(lastPrintedAt)} · Lần in: {printCount}
                </div>
            )}
        </div>
    )
}

WarehouseSlip.propTypes = {
    handover: PropTypes.object.isRequired,
    printInfo: PropTypes.object.isRequired,
}

export default WarehouseSlip
